import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Menu, MenuButton, MenuItem, MenuList, Text } from '@chakra-ui/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import PaginationComponent from '../../Pagination';
import NoDataComponent from '../../NoData';
import TableComponent from '../../Table';
import InputSearchComponent from '../../Common/Form/input-search';
import {
  deleteArticle,
  fetchListMyArticles,
  fetchListWrittingType,
  getDetailArticleEarnings
} from '../../../apis/article.api';
import { formartNumber, formatDate, formatNumber } from '../../../utils/helper.util';
import ImageComponent from '../../Common/Image';
import ChevronDownIcon from '../../../assets/chevron-down-green-icon.svg';
import EyeGreenIcon from '../../../assets/eye-light-green-icon.svg';
import EyeDarkGreenIcon from '../../../assets/eye-dark-green-icon.svg';
import EditIcon from '../../../assets/edit-icon.svg';
import AnalyticIcon from '../../../assets/analytics-icon.svg';
import ShareIcon from '../../../assets/share-green-icon.svg';
import DeleteIcon from '../../../assets/bin-red-icon.svg';
import CustomModal from '../../Modal/CustomModal';
import HelpIcon from '../../../assets/help-green-icon.svg';
import ArrowUpLightGreen from '../../../assets/arrow-up-light-green-icon.svg';
import ShareContentModal from '../../ShareContentModal';
import { fetchListTopic } from '../../../apis/discovery.api';
import { EARNING_TEXT } from '../../../constants/twygs.constant';
import { useNavigate } from 'react-router-dom';
import ConfirmModal from '../../Modal/ConfirmModal';
import DeleteModalIcon from '../../../assets/delete-modal-icon.svg';
import PointIcon from '../../../assets/point-icon.svg';
import InformIcon from '../../../assets/inform-white-icon.svg';
import WriteIcon from '../../../assets/write-icon.svg';
import CommunityIcon from '../../../assets/community-icon.svg';
import NoResultImg from '../../../assets/no-result.svg';
import ArticleFlaggedModal from '../../Modal/ArticleFlaggedModal';
import { convertCountLike } from '../../../constants';
import { TooltipComponent } from '../../Tooltip';
import InvestorsDetailModal from '../../Modal/InvestorDetail/investors-detail.component';
import { useAtom } from 'jotai';
import { profileAtom } from '../../Settings/atom';

export const ArticleTableComponent = () => {
  const navigate = useNavigate();
  const [dataTable, setDataTable] = useState([]);
  const [modal, setModal] = useState('');
  const [detailData, setDetailData] = useState({});
  const [pagination, setPagination] = useState({
    limit: 4,
    page: 1,
    sortBy: undefined,
    sort: undefined,
    search: undefined
  });
  const [type, setType] = useState({
    id: undefined,
    value: ''
  });
  const [category, setCategory] = useState({
    id: undefined,
    value: ''
  });
  const [profile] = useAtom(profileAtom);

  const listAnalytic = [
    {
      id: 1,
      name: 'Total Likes',
      value: detailData?.earnings ? detailData?.reactionNumber : '-'
    },
    {
      id: 2,
      name: 'Total Views',
      value: detailData?.earnings ? detailData?.viewNumber : '-'
    },
    {
      id: 3,
      name: 'Total Comments',
      value: detailData?.earnings ? detailData?.commentNumber : '-'
    },
    {
      id: 4,
      name: 'Total Shares',
      value: detailData?.earnings ? detailData?.shareNumber : '-'
    }
  ];

  const params = {
    ...pagination,
    writingTypeId: type.id,
    topicIds: [category.id]
  };

  const filteredParams = Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined)
  );

  const {
    data: getListArticles,
    isFetching,
    refetch
  } = useQuery({
    queryKey: ['getListArticles', filteredParams],
    queryFn: () => fetchListMyArticles(filteredParams)
  });

  const { data: getListTopic } = useQuery({
    queryKey: ['list-categories'],
    queryFn: fetchListTopic
  });

  const { data: getListWritingType } = useQuery({
    queryKey: ['list-writing-type'],
    queryFn: fetchListWrittingType
  });

  const { mutateAsync: deleteArticleAsync } = useMutation({
    mutationFn: deleteArticle
  });

  const { mutateAsync: getDetailArticleEarningsAsync } = useMutation({
    mutationFn: getDetailArticleEarnings
  });

  const handleChangePage = (page) => {
    setDataTable(() => []);
    setPagination((pre) => {
      return {
        ...pre,
        page
      };
    });
  };

  const handleChangeSearch = (search) => {
    setPagination((pre) => {
      return {
        ...pre,
        page: 1,
        search: search
      };
    });
  };

  const handleChangeType = (id, name) => () => {
    setType({ id: id, value: name });
    setPagination((pre) => {
      return {
        ...pre,
        page: 1
      };
    });
  };

  const handleChangeCategory = (id, name) => () => {
    setCategory({ id: id, value: name });
    setPagination((pre) => {
      return {
        ...pre,
        page: 1
      };
    });
  };

  const handleDeleteArticle = async (id) => {
    try {
      await deleteArticleAsync(id);
      setModal('');
      refetch();
    } catch (error) {
      console.error('Error deleting article:', error);
      throw error;
    }
  };

  const handleShowConfirmDeleteModal = (data) => {
    setDetailData(data);
    setModal('remove-item');
  };

  const convertArticles = useCallback((row) => {
    return (
      <Box
        display={'flex'}
        alignItems={'center'}
        width={{ base: '100vw', md: 'unset' }}
        cursor={'pointer'}
        onClick={() => {
          if (row?.isPublished) {
            navigate(`/articles/${row?.alternativeId || row?.id}`);
          } else {
            navigate(`/article/edit?id=${row?.id}&step=4&type=edit&action=click`);
          }
        }}
      >
        <Box position={'relative'} flexShrink={0}>
          {row?.cover ? (
            <ImageComponent
              rounded={'11.38px'}
              w={'40px'}
              h={'40px'}
              src={row?.cover}
              alt="cover"
            />
          ) : (
            <Box
              rounded={'11.38px'}
              w={'40px'}
              h={'40px'}
              bgColor={'#F2F3F7'}
              display={'flex'}
              justifyContent={'center'}
              alignItems={'center'}
              textAlign={'center'}
              fontSize={'10px'}
              lineHeight={'100%'}
            >
              No Image
            </Box>
          )}
        </Box>
        <Box ml={'12px'} display={'flex'} flexDir={'column'} gap={1}>
          <Text
            fontWeight={800}
            fontSize={'xs'}
            title={row?.title}
            lineHeight={'100%'}
            overflow={'hidden'}
            textOverflow={'ellipsis'}
            wordBreak={'break-word'}
            sx={{
              display: '-webkit-inline-box',
              WebkitLineClamp: '1',
              WebkitBoxOrient: 'vertical'
            }}
            _hover={{
              textDecoration: 'underline'
            }}
          >
            {row?.title || 'Untitled'}
          </Text>
          <Text
            wordBreak={'break-word'}
            fontSize={'xs'}
            title={row?.description}
            overflow={'hidden'}
            textOverflow={'ellipsis'}
            sx={{
              display: '-webkit-inline-box',
              WebkitLineClamp: '2',
              WebkitBoxOrient: 'vertical'
            }}
          >
            {row?.description || 'No description'}
          </Text>
        </Box>
      </Box>
    );
  }, []);

  const convertCategory = useCallback((row) => {
    return (
      <Box display={'flex'} flexWrap={'wrap'} gap={'8px'}>
        {row?.topics.length > 0 ? (
          row?.topics?.slice(0, 3)?.map((item) => (
            <Box
              key={item.id}
              px={'8px'}
              py={'4px'}
              rounded={'80px'}
              bgColor={'#F0EDEC'}
              w={'fit-content'}
              whiteSpace={'nowrap'}
            >
              <Text title={item?.name} color="#6A4E42" fontWeight={800} fontSize={'xs'}>
                {item?.name}
              </Text>
            </Box>
          ))
        ) : (
          <Text fontSize={'xs'}>No Categories</Text>
        )}
      </Box>
    );
  }, []);

  const convertStatus = useCallback((row) => {
    return row?.moderation?.showWarning ? (
      <Box
        py={2}
        px={3}
        display={'flex'}
        gap={1}
        rounded={'80px'}
        bgColor={'#DE1414'}
        w={'91px'}
        alignItems={'center'}
        justifyContent={'center'}
        onClick={() => {
          setModal('article-flagged');
          setDetailData(row);
        }}
        cursor={'pointer'}
      >
        <Text color={'#FFFFFF'} fontWeight={800} fontSize={'xs'}>
          Flagged
        </Text>
        <img className="w-4 h-4" src={InformIcon} alt="" />
      </Box>
    ) : (
      <Box
        py={2}
        px={3}
        rounded={'80px'}
        bgColor={row?.isPublished ? '#4CAF50' : '#EDF7EE'}
        w={'fit-content'}
      >
        <Text
          color={row?.isPublished ? '#FFFFFF' : '#204A22'}
          fontWeight={row?.isPublished ? 800 : 600}
          fontSize={'xs'}
          textAlign={'center'}
        >
          {row?.isPublished ? 'Published' : 'Draft'}
        </Text>
      </Box>
    );
  }, []);

  const convertViews = useCallback(
    (row) => (
      <Box display="flex" gap={2} px={3} py="10px" justifyContent="center">
        {row?.isPublished ? (
          <>
            <img src={EyeDarkGreenIcon} alt="" />
            <Text
              title={row.viewNumber || 'No views'}
              color="#204A22"
              fontWeight={600}
              fontSize="xs"
            >
              {formatNumber(row.viewNumber)}
            </Text>
          </>
        ) : (
          <Text color="#204A22" fontWeight={600} fontSize="xs">
            -
          </Text>
        )}
      </Box>
    ),
    []
  );

  const convertPoints = useCallback(
    (row) => (
      <Box display="flex" justifyContent="center">
        {row?.isPublished ? (
          <Box display="flex" flexDir={'column'} gap={1} justifyContent="center">
            <Box display="flex" gap={1}>
              <img src={PointIcon} className="w-5 h-5" alt="" />
              <Text
                title={row?.reputationPoint?.amount || 'No twigs'}
                color="#204A22"
                fontWeight={800}
                fontSize="xs"
              >
                {formatNumber(row?.reputationPoint?.amount)}{' '}
                {row?.reputationPoint?.amount === 1 ? 'Twig' : 'Twigs'}
              </Text>
            </Box>
            <Text color="#204A22" fontSize="xs">
              From {row?.reputationPoint?.count}{' '}
              {row?.reputationPoint?.count === 1 ? 'Reader' : 'Readers'}
            </Text>
          </Box>
        ) : (
          <Text color="#204A22" fontWeight={600} fontSize="xs">
            -
          </Text>
        )}
      </Box>
    ),
    []
  );

  const convertInvestors = useCallback((row) => {
    return (
      <Box display="flex" justifyContent="center">
        {row?.isPublished ? (
          <Box display={'flex'} alignItems={'center'} gap={1}>
            <img src={CommunityIcon} className="w-5 h-5" alt="" />
            <Text fontSize={'xs'} fontWeight={600} whiteSpace={'nowrap'}>
              {row?.investmentData?.totalInvestors || 0}{' '}
              {row?.investmentData?.totalInvestors > 1 ? 'Investors' : 'Investor'}
            </Text>
            <img
              src={EyeGreenIcon}
              alt=""
              className="cursor-pointer"
              onClick={() => {
                setModal('investor-detail');
                setDetailData(row);
              }}
            />
          </Box>
        ) : (
          <Text color="#204A22" fontWeight={600} fontSize="xs">
            -
          </Text>
        )}
      </Box>
    );
  }, []);

  const convertAction = useCallback((row) => {
    return (
      <Box display="flex" alignItems={'center'}>
        <img
          src={EditIcon}
          className="p-2 cursor-pointer hover:opacity-80"
          alt="icon"
          title="Edit Article"
          onClick={() => navigate(`/article/edit?id=${row?.id}&step=4&type=edit&action=click`)}
        />
        <img
          src={AnalyticIcon}
          className={`${row?.isPublished ? '' : 'hidden'} p-2 cursor-pointer hover:opacity-80`}
          alt="icon"
          title="View Analytics"
          onClick={() => {
            setModal('analytic');
            setDetailData(row);
          }}
        />
        <img
          src={ShareIcon}
          className={`${row?.isPublished ? '' : 'hidden'} p-2 cursor-pointer hover:opacity-80`}
          alt="icon"
          title="Share Article"
          onClick={() => {
            setModal('share-article');
            setDetailData(row);
          }}
        />
        <img
          src={DeleteIcon}
          className="p-2 cursor-pointer hover:opacity-80"
          alt="icon"
          title="Delete Article"
          onClick={() => handleShowConfirmDeleteModal(row)}
        />
      </Box>
    );
  }, []);

  const handleSort = (sortName) => {
    let objSort = {
      sortBy: pagination.sortBy,
      sort: pagination.sort
    };
    if (sortName !== objSort.sortBy) objSort = { sortBy: sortName, sort: 'asc' };
    else if (sortName === objSort.sortBy && objSort.sort === 'asc')
      objSort = {
        ...objSort,
        sort: 'desc'
      };
    else if (sortName === objSort.sortBy && objSort.sort === 'desc')
      objSort = {
        ...objSort,
        sort: 'asc'
      };
    setPagination({ ...pagination, ...objSort });
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Articles',
        accessor: (row) => convertArticles(row),
        id: 'title',
        isSortField: true,
        widthCustom: 'calc(100% - 35.5rem)',
        minWidthCustom: '12rem'
      },
      {
        Header: 'Categories',
        accessor: (row) => convertCategory(row),
        id: 'contentCategory',
        widthCustom: '10rem',
        minWidthCustom: '10rem'
      },
      {
        Header: 'Views',
        accessor: (row) => convertViews(row),
        id: 'viewNumber',
        widthCustom: '7rem',
        isSortField: true,
        minWidthCustom: '7rem'
      },
      {
        Header: 'Twigs',
        accessor: (row) => convertPoints(row),
        id: 'investmentPoints',
        widthCustom: '8rem',
        isSortField: true,
        minWidthCustom: '8rem'
      },
      {
        Header: 'Investors',
        accessor: (row) => convertInvestors(row),
        id: 'investors',
        widthCustom: '8rem',
        minWidthCustom: '8rem',
        isSortField: true
      },
      {
        Header: 'Status',
        accessor: (row) => convertStatus(row),
        id: 'isPublished',
        widthCustom: '7rem',
        minWidthCustom: '7rem'
      },
      {
        Header: 'Date Created',
        accessor: (row) => <Text fontSize={'xs'}>{formatDate(row?.createdAt, true)}</Text>,
        id: 'createdAt',
        widthCustom: '10rem',
        minWidthCustom: '10rem',
        isSortField: true
      },
      {
        Header: 'Actions',
        accessor: (row) => convertAction(row),
        widthCustom: '12rem',
        minWidthCustom: '12rem',
        id: 'action'
      }
    ],
    [convertArticles, convertCategory, convertStatus, convertViews, convertAction, handleSort]
  );

  useEffect(() => {
    if (getListArticles) {
      setDataTable(getListArticles?.data);
    }
  }, [getListArticles, type.id, category.id]);

  useEffect(() => {
    (async () => {
      if (modal === 'analytic') {
        const response = await getDetailArticleEarningsAsync(detailData.id);
        setDetailData({
          ...detailData,
          earnings: response?.earnings || '0',
          totalInvestors: response?.totalInvestors || '0',
          totalInvestedPoints: response?.totalInvestedPoints || '0'
        });
      }
    })();
  }, [modal]);

  return (
    <Box
      w={'full'}
      border={'1px solid'}
      borderColor={'anti-flash-lite'}
      borderRadius={{ base: 0, md: '12px' }}
      shadow={'outline'}
    >
      <Box
        bg={'white'}
        p={{ base: '16px', md: '16px 24px' }}
        display={'flex'}
        alignItems={'start'}
        borderTopRadius={'12px'}
        justifyContent={'space-between'}
        overflow={'auto'}
      >
        <Box
          display={'flex'}
          gap="12px"
          flexDirection={{ base: 'column', md: 'row' }}
          width={'full'}
          overflow={'auto'}
        >
          <InputSearchComponent
            setPaginationSearch={handleChangeSearch}
            placeholder={'Search articles'}
            setLoading={() => {}}
            width={{ base: '100%', md: '320px' }}
          />
          <div className="flex gap-3">
            <Menu>
              <MenuButton
                as={Button}
                rightIcon={
                  <div className="ml-[16px]">
                    <img src={ChevronDownIcon} alt="icon" />
                  </div>
                }
                height={'36px'}
                w={{ base: '100%', md: 'fit-content' }}
                variant={'growth-green-solid'}
                px={'18px'}
              >
                <Text color={'#4CAF50'} fontWeight={900} textAlign={'start'} fontSize={'sm'}>
                  {category.value === '' ? 'Category' : category.value}
                </Text>
              </MenuButton>
              <MenuList
                p={'0.5rem'}
                borderRadius={'0.5rem'}
                minW={'130px'}
                shadow={'md'}
                border={'1px solid'}
                borderColor={'light-gray'}
                maxH={'300px'}
                overflow={'auto'}
                css={{
                  '&::-webkit-scrollbar': {
                    width: '6px'
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#e6e7eb',
                    borderRadius: '30px'
                  }
                }}
              >
                {[
                  { id: undefined, name: 'All Categories', desc: '' },
                  ...(getListTopic || [])
                ]?.map((item, index) => {
                  return (
                    <MenuItem
                      key={index}
                      minW={'114px'}
                      onClick={handleChangeCategory(item.id, item.name)}
                      p={'8px 8px 8px 10px'}
                      _focus={{ bgColor: 'white' }}
                      _hover={{
                        bgColor: 'anti-flash-white',
                        '& .child-box': {
                          display: 'block'
                        }
                      }}
                      rounded={'0.5rem'}
                      fontSize={'14px'}
                      lineHeight={'20px'}
                      fontWeight={'800'}
                    >
                      {item.name}
                    </MenuItem>
                  );
                })}
              </MenuList>
            </Menu>
            <Menu>
              <MenuButton
                as={Button}
                rightIcon={
                  <div className="ml-[16px]">
                    <img src={ChevronDownIcon} alt="icon" />
                  </div>
                }
                w={{ base: '100%', md: 'fit-content' }}
                height={'36px'}
                variant={'growth-green-solid'}
                px={'18px'}
              >
                <Text color={'#4CAF50'} fontWeight={900} fontSize={'sm'} textAlign={'start'}>
                  {type.value === '' ? 'Type' : type.value}
                </Text>
              </MenuButton>
              <MenuList
                p={'0.5rem'}
                borderRadius={'0.5rem'}
                minW={'130px'}
                maxH={'300px'}
                overflow={'auto'}
                css={{
                  '&::-webkit-scrollbar': {
                    width: '6px'
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#e6e7eb',
                    borderRadius: '30px'
                  }
                }}
                shadow={'md'}
                border={'1px solid'}
                borderColor={'light-gray'}
              >
                {[
                  { id: undefined, name: 'All Types', desc: '' },
                  ...(getListWritingType || [])
                ]?.map((item, index) => {
                  return (
                    <MenuItem
                      key={index}
                      minW={'114px'}
                      onClick={handleChangeType(item.id, item.name)}
                      p={'8px 8px 8px 10px'}
                      _focus={{ bgColor: 'white' }}
                      _hover={{
                        bgColor: 'anti-flash-white',
                        '& .child-box': {
                          display: 'block'
                        }
                      }}
                      rounded={'0.5rem'}
                      fontSize={'14px'}
                      lineHeight={'20px'}
                      fontWeight={'800'}
                    >
                      {item.name}
                    </MenuItem>
                  );
                })}
              </MenuList>
            </Menu>
          </div>
        </Box>
      </Box>
      <Box
        bg={'white'}
        rounded={'0 0 0.75rem 0.75rem'}
        display={'flex'}
        minH={'380px'}
        flexFlow={'column'}
        overflowY={'hidden'}
        overflowX={'auto'}
        css={{
          '&::-webkit-scrollbar': {
            display: 'block',
            height: '8px' // For horizontal scrollbar
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#e6e7eb',
            borderRadius: '30px'
          },
          '&::-webkit-scrollbar-track': {
            background: '#f5f5f5'
          }
        }}
      >
        <TableComponent
          isLoading={isFetching}
          columns={columns}
          data={dataTable || []}
          headerHeight={'44px'}
          fontsizeTable={'sm'}
          handleSort={handleSort}
          currentSort={{ sortBy: pagination.sortBy, sort: pagination.sort }}
          haveFooter={false}
        />
        {!pagination.search && dataTable?.length === 0 && !isFetching ? (
          <Box minH={'380px'} display={'flex'} alignItems={'center'} justifyContent={'center'}>
            <NoDataComponent
              title="It looks like you haven’t written any articles yet."
              description="Share your story and become part of our growing community!"
              titleButton={profile?.isCreator ? 'Write' : undefined}
              iconButton={profile?.isCreator ? WriteIcon : undefined}
              onClick={() => navigate('/article/create')}
              isFetching={isFetching}
            />
          </Box>
        ) : null}
        {pagination.search && dataTable?.length === 0 && !isFetching ? (
          <Box minH={'380px'} display={'flex'} alignItems={'center'} justifyContent={'center'}>
            <NoDataComponent
              icon={NoResultImg}
              title="No results found"
              description="Try a different search term"
              isFetching={isFetching}
            />
          </Box>
        ) : null}
      </Box>

      {getListArticles?.totalPage > 0 ? (
        <Box
          borderBottomRadius={'12px'}
          p={'1.25rem 1.5rem'}
          mt={'auto'}
          borderTop="1px solid"
          borderColor={'anti-flash-lite'}
          background={'light.100'}
        >
          <PaginationComponent
            currentPage={pagination.page}
            totalPages={getListArticles?.totalPage}
            onChangePage={handleChangePage}
          />
        </Box>
      ) : null}
      {modal === 'analytic' ? (
        <CustomModal
          isOpen
          title="Article Analytics"
          onClick={() => setModal('')}
          hideFooter
          minW={{ base: 'unset', md: '708px' }}
        >
          <div className="flex flex-wrap gap-4 max-h-[400px] overflow-y-auto">
            {listAnalytic?.map((item) => {
              return (
                <div
                  key={item.id}
                  className="bg-white w-full md:w-fit p-6 rounded-[12px] border border-05-seasalt-white shadow-[0px_4px_4px_0px_#F2F3F780]"
                >
                  <p className="font-[800]">{item?.name}</p>
                  <p className="font-[800] text-[32px] mt-2">{convertCountLike(item?.value)}</p>
                </div>
              );
            })}
            <div className="bg-white w-full md:w-fit p-6 rounded-[12px] border border-05-seasalt-white shadow-[0px_4px_4px_0px_#F2F3F780]">
              <p className="font-[800]">Total Twigs</p>
              <p className="font-[800] text-growth-green-900 text-[32px] mt-2">
                {detailData?.earnings ? convertCountLike(detailData?.reputationPoint?.amount) : '-'}
              </p>
              <p className="text-growth-green-900 text-sm">
                From {detailData.reputationPoint.count}{' '}
                {detailData.reputationPoint.count === 1 ? 'Reader' : 'Readers'}
              </p>
            </div>
            <div className="bg-white w-full md:w-fit p-6 rounded-[12px] border border-05-seasalt-white shadow-[0px_4px_4px_0px_#F2F3F780]">
              <p className="font-[800]">Total Investors</p>
              <p className="font-[800] text-growth-green-900 text-[32px] mt-2">
                {detailData.totalInvestors ? convertCountLike(detailData.totalInvestors) : '-'}
              </p>
              <p className="text-growth-green-900 text-sm">
                Invested {detailData.totalInvestedPoints || 0}{' '}
                {detailData.totalInvestedPoints === 1 ? 'Seed' : 'Seeds'}
              </p>
            </div>
            <div className="bg-white p-6 rounded-[12px] border border-05-seasalt-white shadow-[0px_4px_4px_0px_#F2F3F780] w-full md:w-[296px]">
              <div className="flex items-center gap-2">
                <p className="font-[800]">Article Earnings</p>
                <TooltipComponent
                  maxW={{ base: '220px', md: '330px' }}
                  description={EARNING_TEXT}
                  fontSize={'10px'}
                  color={'jet-black'}
                  placement={'top'}
                  distance={{ base: '150px', md: '0' }}
                  Icon={HelpIcon}
                />
              </div>
              <div className="flex items-center justify-between gap-[18px] mt-2">
                <p className="text-growth-green-900 text-[28px] font-[800]">
                  ${formartNumber(detailData?.earnings || 0, 2)}
                </p>
                <a
                  className="flex gap-2 items-center hover:opacity-80 cursor-pointer"
                  href="/creator-dashboard/earnings/my-earnings"
                >
                  <p className="text-growth-green-500 text-sm font-[900]">For more details</p>
                  <img className="fill-growth-green-500" src={ArrowUpLightGreen} alt="" />
                </a>
              </div>
            </div>
          </div>
        </CustomModal>
      ) : null}
      {modal === 'article-flagged' ? (
        <ArticleFlaggedModal
          isOpen
          onClose={() => setModal('')}
          articleId={detailData?.id}
          primarySource={detailData?.moderation.primarySource}
          primaryViolation={detailData?.moderation.primaryViolation}
        />
      ) : null}
      {modal === 'share-article' ? (
        <ShareContentModal
          isOpen
          title="Share Article"
          smallTitle="Share this Article!"
          shareUrl={`${process.env.REACT_APP_NEWSLETTER_URL}articles/${detailData?.alternativeId || detailData?.id}`}
          media={detailData?.cover}
          articleId={detailData?.alternativeId || detailData?.id}
          article={detailData}
          onClick={() => setModal('')}
          titleBtn="Done"
          minW="439px"
        />
      ) : null}
      {modal === 'remove-item' ? (
        <ConfirmModal
          isOpen={true}
          Icon={DeleteModalIcon}
          title={'Delete Article?'}
          desc={'This action will permanently delete your article'}
          titleBtn={'Delete'}
          onClick={() => {
            handleDeleteArticle(detailData?.id);
          }}
          isLoading={false}
          onClose={() => {
            setModal('');
          }}
        />
      ) : null}
      {modal === 'investor-detail' ? (
        <InvestorsDetailModal isOpen onClose={() => setModal('')} articleId={detailData?.id} />
      ) : null}
    </Box>
  );
};
